import { useQuery } from "@tanstack/react-query";
import { fetchApi } from "@/lib/api";
import { CLERK_AVAILABLE, getDemoAuthHeaders, getClerkAuthHeaders } from "@/lib/auth";
import { useAuthConditional } from "@/lib/use-auth-conditional";
import type { ProjectCostCode } from "./useCostCodes";

/**
 * Mantine Select option format
 */
export interface CostCodeSelectOption {
  value: string;
  label: string;
  group: string;
}

/**
 * Response from GET /api/projects/:id/cost-codes
 * NOTE: API returns "costCodes" not "projectCostCodes"
 */
interface ProjectCostCodesResponse {
  costCodes: ProjectCostCode[];
}

/**
 * Hook to fetch cost codes assigned to a specific project.
 * Returns raw data for general use.
 *
 * @param projectId - The project ID to fetch cost codes for
 * @returns Query result with projectCostCodes array
 */
export function useProjectCostCodes(projectId: string) {
  const auth = useAuthConditional();

  return useQuery({
    queryKey: ["projects", projectId, "costCodes"],
    queryFn: async () => {
      const headers = CLERK_AVAILABLE
        ? await getClerkAuthHeaders(auth.getToken)
        : getDemoAuthHeaders();

      const response = await fetchApi<ProjectCostCodesResponse>(
        `/api/projects/${projectId}/cost-codes`,
        { headers }
      );

      // Return normalized shape
      return {
        projectCostCodes: response.costCodes,
      };
    },
    enabled: auth.isSignedIn && !!projectId,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}

/**
 * Hook to fetch cost codes for a project formatted for Mantine Select component.
 *
 * Returns options in format: { value: costCodeId, label: "code - name", group: "division" }
 * Options are grouped by division for better UX.
 *
 * Example usage:
 * ```tsx
 * const { data, isLoading, error } = useProjectCostCodeOptions(projectId);
 *
 * <Select
 *   data={data?.options || []}
 *   label="Cost Code"
 *   searchable
 *   error={error?.message}
 * />
 * ```
 *
 * @param projectId - The project ID to fetch cost codes for
 * @returns Query result with formatted options and grouping info
 */
export function useProjectCostCodeOptions(projectId: string) {
  const auth = useAuthConditional();

  return useQuery({
    queryKey: ["projects", projectId, "costCodes", "selectOptions"],
    queryFn: async () => {
      const headers = CLERK_AVAILABLE
        ? await getClerkAuthHeaders(auth.getToken)
        : getDemoAuthHeaders();

      const response = await fetchApi<ProjectCostCodesResponse>(
        `/api/projects/${projectId}/cost-codes`,
        { headers }
      );

      // Transform to Mantine Select format with division grouping
      const options: CostCodeSelectOption[] = response.costCodes.map((pcc) => ({
        value: pcc.costCode.id,
        label: `${pcc.costCode.code} - ${pcc.costCode.name}`,
        group: pcc.costCode.division,
      }));

      // Sort by division first, then by code
      options.sort((a, b) => {
        if (a.group !== b.group) {
          return a.group.localeCompare(b.group);
        }
        return a.label.localeCompare(b.label);
      });

      // Extract unique divisions for metadata
      const divisions = Array.from(
        new Set(response.costCodes.map((pcc) => pcc.costCode.division))
      ).sort();

      return {
        options,
        divisions,
        totalCodes: response.costCodes.length,
      };
    },
    enabled: auth.isSignedIn && !!projectId,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}

/**
 * Helper function to find a cost code by ID from project cost codes
 * Useful for displaying selected cost code details
 */
export function useFindProjectCostCode(projectId: string, costCodeId: string | null) {
  const { data } = useProjectCostCodes(projectId);

  if (!costCodeId || !data?.projectCostCodes) {
    return null;
  }

  return data.projectCostCodes.find((pcc) => pcc.costCode.id === costCodeId);
}
